import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { CreatePayrollDTO } from "@/modules/hr/payroll/domain/types";
import {
  computeTotals,
  formValuesToItems,
  PayrollEntryForm,
  PayrollEntryHeader,
} from "../ui/PayrollEntryForm";
import type { PayrollFormValues } from "../ui/PayrollEntryForm";
import { usePayrollCreateMutation, usePayrollStatusMutation } from "../api/queries";
import { PayrollNewTour, usePayrollNewTour } from "../tour/PayrollNewTour";

const MONTH_NAMES = [
  "",
  "ມ.ກ.",
  "ກ.ພ.",
  "ມ.ນ.",
  "ມ.ສ.",
  "ພ.ສ.",
  "ມ.ຖ.",
  "ກ.ກ.",
  "ສ.ຫ.",
  "ກ.ຍ.",
  "ຕ.ລ.",
  "ພ.ຈ.",
  "ທ.ວ.",
];

export function PayrollNewPage() {
  const search = useSearch({ from: "/app/hr/payroll/period/new" }) as {
    year?: number;
    month?: number;
  };
  const nav = useNavigate();
  const createMutation = usePayrollCreateMutation();
  const statusMutation = usePayrollStatusMutation();

  const y = Number(search.year ?? new Date().getFullYear());
  const m = Number(search.month ?? new Date().getMonth() + 1);
  const periodLabel =
    Number.isInteger(y) && Number.isInteger(m) && MONTH_NAMES[m]
      ? `${MONTH_NAMES[m]} ${y}`
      : `${y}/${m}`;

  const isSubmitting = createMutation.isPending || statusMutation.isPending;

  const handleCancel = () => {
    nav({
      to: "/app/hr/payroll/period",
      search: {
        year: y,
        month: m,
        departmentId: undefined,
        status: undefined,
        search: undefined,
        sort: undefined,
      },
    });
  };

  const buildCreateDto = (values: PayrollFormValues): CreatePayrollDTO => {
    const totals = computeTotals(values);
    const items = formValuesToItems(values);
    const ot = Number(String(values.otAmount).replace(/,/g, "")) || 0;
    const bonus = Number(String(values.bonusAmount).replace(/,/g, "")) || 0;
    return {
      employeeId: values.employeeId,
      periodYear: y,
      periodMonth: m,
      baseSalary: totals.baseSalary,
      otAmount: ot,
      bonusAmount: bonus,
      totalEarnings: totals.totalEarnings,
      totalDeductions: totals.totalDeductions,
      netSalary: totals.netSalary,
      notes: values.notes.trim() || null,
      items,
    };
  };

  const handleSaveDraft = (values: PayrollFormValues) => {
    if (!values.employeeId?.trim()) return;
    createMutation.mutate(buildCreateDto(values), {
      onSuccess: (data) => {
        nav({ to: "/app/hr/payroll/$id/edit", params: { id: data.id } });
      },
    });
  };

  const handleSaveAndConfirm = (values: PayrollFormValues) => {
    if (!values.employeeId?.trim()) return;
    createMutation.mutate(buildCreateDto(values), {
      onSuccess: (data) => {
        statusMutation.mutate(
          { id: data.id, status: "confirmed" },
          {
            onSuccess: () => {
              nav({ to: "/app/hr/payroll/$id/edit", params: { id: data.id } });
            },
          },
        );
      },
    });
  };

  const tour = usePayrollNewTour();

  return (
    <>
      <Header />
      <Main>
        <PayrollNewTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        <div data-tourid="payroll-new-back">
          <PayrollEntryHeader
            periodLabel={periodLabel}
            title="ເອກະສານເງິນເດືອນພະນັກງານ"
            subtitle="ສ້າງລາຍການເງິນເດືອນໃໝ່ສໍາລັບໄລຍະນີ້"
            onBack={handleCancel}
            onStartTour={tour.startTour}
          />
        </div>
        <div data-tourid="payroll-new-form">
          <PayrollEntryForm
          mode="new"
          periodYear={y}
          periodMonth={m}
          onCancel={handleCancel}
          onSaveDraft={handleSaveDraft}
          onSaveAndConfirm={handleSaveAndConfirm}
          isSubmitting={isSubmitting}
        />
        </div>
      </Main>
    </>
  );
}
